import { get, patch } from './client'
import { getQuotaPerUnit } from '@/utils/quota'

/** 后端 PricingValues：以既有 option key 命名的单模型定价 */
export type PricingValues = Record<string, number | string>

export interface ModelPricingEntry {
  model_name: string
  version: string
  /** 显式配置过的键（sparse） */
  configured: PricingValues
  /** 叠加引擎默认后的实际生效值 */
  effective: PricingValues
  cache_write_mode?: string
  billing_details?: Record<string, unknown>
  usage_schema?: Record<string, unknown>
}

export interface ModelPricingSnapshot {
  entries: ModelPricingEntry[]
  options: Record<string, string>
  empty_version: string
}

export interface ModelPricingChange {
  model_name: string
  expected_version: string
  pricing: PricingValues
  reset?: boolean
}

/**
 * GET /api/option/model_pricing
 * 返回每个模型的 configured（已配置）与 effective（实际生效，含引擎默认）+ version。
 */
export async function getModelPricingSnapshotApi(names?: string[]): Promise<ModelPricingSnapshot> {
  return get('/api/option/model_pricing', names?.length ? { model: names } : undefined)
}

/** 版本冲突：别人已经改过这个模型 */
export class ModelPricingConflictError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ModelPricingConflictError'
  }
}

/**
 * PATCH /api/option/model_pricing
 * 按模型带 expected_version 提交，服务端冲突时返回 409。
 */
export async function patchModelPricingApi(changes: ModelPricingChange[]): Promise<string[]> {
  try {
    const data = await patch<{ updated_models?: string[] }>('/api/option/model_pricing', {
      changes,
    })
    return data?.updated_models ?? []
  } catch (err) {
    const e = err as {
      response?: { status?: number; data?: { message?: string } }
      message?: string
    }
    if (e?.response?.status === 409) {
      throw new ModelPricingConflictError(
        e.response?.data?.message || '该模型定价已被其他人修改，请刷新后重试'
      )
    }
    throw err
  }
}

/**
 * ratio → “$/1M tokens” 的换算系数：price = ratio × 1e6 / quotaPerUnit。
 * 默认实例 quotaPerUnit=500000，即 ratio×2；改过额度单位的实例自动跟随。
 */
export const ratioToPriceFactor = (quotaPerUnit = getQuotaPerUnit()): number =>
  1_000_000 / (quotaPerUnit > 0 ? quotaPerUnit : 500000)

const num = (v: unknown): number | null => {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export interface EffectivePricing {
  /** 每百万 token 的输入价（USD）；有固定价时为 null */
  inputPerMillion: number | null
  /** 每百万 token 的输出价（USD） */
  outputPerMillion: number | null
  cacheReadPerMillion: number | null
  cacheWritePerMillion: number | null
  /** 按次固定价（USD） */
  fixedPrice: number | null
  /** 有多少个键是引擎默认（不是显式配置） */
  defaultedKeys: string[]
}

/**
 * 从 effective PricingValues 推导出人可读的单价。
 * 与后端一致：生效价 = 默认值叠加显式配置。
 */
export function describeEffectivePricing(
  effective: PricingValues | undefined,
  configured?: PricingValues,
  quotaPerUnit = getQuotaPerUnit()
): EffectivePricing {
  const factor = ratioToPriceFactor(quotaPerUnit)
  const values = effective ?? {}
  const fixedPrice = num(values.ModelPrice)
  const modelRatio = num(values.ModelRatio)
  const completionRatio = num(values.CompletionRatio)
  const cacheRatio = num(values.CacheRatio)
  const createCacheRatio = num(values.CreateCacheRatio)

  const base = modelRatio !== null ? modelRatio * factor : null
  const configuredKeys = new Set(Object.keys(configured ?? {}))
  const defaultedKeys = Object.keys(values).filter((k) => !configuredKeys.has(k))

  return {
    fixedPrice,
    inputPerMillion: base,
    outputPerMillion: base !== null && completionRatio !== null ? base * completionRatio : null,
    cacheReadPerMillion: base !== null && cacheRatio !== null ? base * cacheRatio : null,
    cacheWritePerMillion:
      base !== null && createCacheRatio !== null ? base * createCacheRatio : null,
    defaultedKeys,
  }
}

/**
 * 估价：给定 token 用量，算出扣的 quota 与美元。
 * 公式与后端一致：quota = tokens × ratio × 分组倍率；固定价优先。
 */
export function estimateQuota(
  pricing: EffectivePricing,
  usage: {
    promptTokens: number
    completionTokens: number
    cacheReadTokens: number
    cacheWriteTokens: number
  },
  groupRatio = 1,
  quotaPerUnit = getQuotaPerUnit()
): { quota: number; usd: number } {
  const ratio = groupRatio > 0 ? groupRatio : 1
  let usd: number
  if (pricing.fixedPrice !== null && pricing.fixedPrice > 0) {
    // 按次计费：固定价优先，与后端一致
    usd = pricing.fixedPrice * ratio
  } else {
    const perMillion =
      usage.promptTokens * (pricing.inputPerMillion ?? 0) +
      usage.completionTokens * (pricing.outputPerMillion ?? 0) +
      usage.cacheReadTokens * (pricing.cacheReadPerMillion ?? 0) +
      usage.cacheWriteTokens * (pricing.cacheWritePerMillion ?? 0)
    usd = (perMillion / 1_000_000) * ratio
  }
  const quota = usd * quotaPerUnit
  return { quota: Math.round(quota), usd }
}
